/* eslint-disable @typescript-eslint/no-explicit-any */
// ==============================
// Imports
// ==============================

// MUI Components
import AppBarSpacer from '../../components/common/AppBarSpacer';
import { Box } from '@mui/material';
import PageHeader from '../../components/common/PageHeader';
import Grid from '@mui/material/Grid2';

// Libraries
import { useEffect, useState } from 'react';
import { TrendingUp, Users, UserMinus } from 'lucide-react';

// Local components
import MiniDrawer from '../../components/MiniDrawer';
import PeriodSelector from './PeriodSelector';
import { PeriodEnum, Stats } from '../../types/Report.type';
import StatCard from './StatCard';
import RevenueChart from './RevenueChart';
import { apiGet } from '../../utils/apiClient';
import { SuccessResponse } from '../../types/CourseStageAccordion.type';
import CustomerTable from './CustomerTable';
import { Dayjs } from 'dayjs';
import RegistrationTable from './RegistrationTable';
import TrialTable from './TrialTable';
import { useTranslation } from '../../i18n';

const ReportLayout = () => {
  const { t } = useTranslation();

  // States
  const [open, setOpen] = useState(true);
  const [period, setPeriod] = useState<PeriodEnum>(PeriodEnum.MONTHLY);

  // Data
  const [revenue, setRevenue] = useState<number>(0);
  const [newCustomers, setNewCustomers] = useState<number>(0);
  const [archivedCustomers, setArchivedCustomers] = useState<number>(0);
  const [revenueByService, setRevenueByService] = useState<
    { name: string; value: number }[]
  >([]);
  const [monthAndYear, setMonthAndYear] = useState<Dayjs | null>(null);

  const handlePeriodChange = (newPeriod: PeriodEnum) => {
    setPeriod(newPeriod);
  };

  // Fetch chart data
  const fetchChartData = async (date: Dayjs | null) => {
    try {
      // Fetch revenue by service type
      const revenueByServiceData = await apiGet<SuccessResponse>(
        `/report/get-service-revenue-chart?month=${
          date ? date.format('MM') : new Date().getMonth() + 1
        }&year=${date ? date.format('YYYY') : new Date().getFullYear()}`,
      );
      setRevenueByService(revenueByServiceData.data);
    } catch (error) {
      console.error('Error fetching chart data:', error);
    }
  };

  const fetchMainData = async () => {
    try {
      // Fetch customer stats
      const newCustomersData = await apiGet<SuccessResponse>(
        '/report/get-new-customers',
      );
      setNewCustomers(newCustomersData.data);

      const archivedCustomersData = await apiGet<SuccessResponse>(
        '/report/get-archived-customers',
      );
      setArchivedCustomers(archivedCustomersData.data);
    } catch (error) {
      console.error('Error fetching main dashboard data:', error);
    }
  };

  useEffect(() => {
    const fetchRevenueData = async () => {
      try {
        // Fetch revenue for selected period
        const revenueData = await apiGet<SuccessResponse>(
          `/report/get-revenue?period=${period}`,
        );
        setRevenue(revenueData.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };

    fetchRevenueData();
  }, [period]);

  useEffect(() => {
    // Fetch chart data
    fetchChartData(monthAndYear);
    fetchMainData();
  }, []);

  // Stats for dashboard cards
  const stats: Stats[] = [
    {
      title:
        period === PeriodEnum.DAILY
          ? t('report.totalRevenueToday')
          : period === PeriodEnum.WEEKLY
            ? t('report.totalRevenueThisWeek')
            : t('report.totalRevenueThisMonth'),
      value: new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND',
        maximumFractionDigits: 0,
      }).format(revenue),
      icon: <TrendingUp size={20} />,
      color: '#1976d2',
    },
    {
      title: t('report.newCustomersThisMonth'),
      value: newCustomers,
      icon: <Users size={20} />,
      color: '#2e7d32',
    },
    {
      title: t('report.archivedCustomers'),
      value: archivedCustomers,
      icon: <UserMinus size={20} />,
      color: '#ed6c02',
    },
  ];

  // ==============================
  // Render
  // ==============================
  return (
    <>
      <div className="flex h-screen">
        {/* Sidebar */}
        <MiniDrawer open={open} setOpen={setOpen} />

        {/* Main content */}
        <main className="flex-1 overflow-auto app-page-bg">
          {/* Offset for fixed AppBar (height = 64px) */}
          <AppBarSpacer />

          <div className="h-full px-6 py-4">
            <PageHeader
              title={t('report.title')}
              subtitle={t('report.subtitle')}
            />

            {/* Content */}
            <Grid container spacing={3}>
              {/* Period selector */}
              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <PeriodSelector
                    period={period}
                    onChange={handlePeriodChange}
                  />
                </Box>
              </Grid>

              {/* Stats cards */}
              {stats.map((stat, index) => (
                <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
                  <StatCard stat={stat} />
                </Grid>
              ))}

              {/* Registration table */}
              <Grid size={{ xs: 12 }}>
                <RegistrationTable />
              </Grid>

              {/* Trial report table */}
              <Grid size={{ xs: 12 }}>
                <TrialTable />
              </Grid>

              {/* Revenue chart */}
              <Grid size={{ xs: 12 }}>
                <RevenueChart
                  title={t('report.totalRevenueByServiceType')}
                  data={revenueByService}
                  monthAndYear={monthAndYear}
                  setMonthAndYear={setMonthAndYear}
                  fetchChartData={fetchChartData}
                />
              </Grid>

              {/* Customer table */}
              <Grid size={{ xs: 12 }} mb={3}>
                <CustomerTable />
              </Grid>
            </Grid>
          </div>
        </main>
      </div>
    </>
  );
};

export default ReportLayout;
